import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { ArrowDownward } from '@mui/icons-material';
import { useChartTokens } from '../../../dashboard/chartTokens';
import { formatINR, formatNumber, formatPct } from '../../../dashboard/primitives';
import { SalesFunnel } from '../../../types/salesAnalytics';
import { FUNNEL_STEP_LABELS } from '../../../pages/sales/salesReportsHelpers';

interface FunnelSectionProps {
  funnel: SalesFunnel | null;
  loading: boolean;
}

/**
 * Horizontal funnel, widest stage first, one series (volume) so no legend is
 * needed per the dataviz rule — the title names it. Built from Box bars
 * rather than a recharts BarChart because the thing that actually matters
 * here — the cohort conversion badge *between* two bars — has no clean
 * equivalent in a standard bar chart.
 *
 * `count` (bar length) and `cohortCount` (conversion basis) are deliberately
 * both shown — see SalesAnalytics.js's getFunnel: 14 opportunities by volume
 * vs 3 by cohort is real, and collapsing them into one number misleads either
 * a capacity read or a conversion read.
 */
const FunnelSection: React.FC<FunnelSectionProps> = ({ funnel, loading }) => {
  const t = useChartTokens();
  const accent = t.series(0);

  if (loading || !funnel) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, mb: 3, minHeight: 320 }} />
    );
  }

  const steps = funnel.steps;
  const maxCount = Math.max(...steps.map((s) => s.count), 1);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, mb: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.25 }}>Sales Funnel</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {funnel.cohortNote}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {steps.map((step, i) => {
          const widthPct = Math.max((step.count / maxCount) * 100, step.count > 0 ? 3 : 0);
          return (
            <Box key={step.step}>
              {i > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 1, py: 0.5 }}>
                  <ArrowDownward sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">
                    {step.conversionFromPrevPct === null
                      ? 'no prior-stage cohort to convert from'
                      : `${formatPct(step.conversionFromPrevPct)} of cohort converted`}
                    {step.enteredDirectly ? ` · +${formatNumber(step.enteredDirectly)} entered directly at this stage` : ''}
                  </Typography>
                </Box>
              )}

              <Tooltip
                title={
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Volume this period: {formatNumber(step.count)}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Traced cohort: {formatNumber(step.cohortCount)}
                    </Typography>
                    {step.value !== null && (
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Value: {formatINR(step.value)}
                      </Typography>
                    )}
                    {step.cohortRetentionPct !== null && (
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {formatPct(step.cohortRetentionPct)} of original cohort retained
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ width: 128, flexShrink: 0, fontWeight: 600 }}>
                    {FUNNEL_STEP_LABELS[step.step] ?? step.step}
                  </Typography>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        height: 30,
                        borderRadius: 1.5,
                        width: `${widthPct}%`,
                        minWidth: step.count > 0 ? 8 : 0,
                        backgroundColor: accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: 1,
                        transition: 'width .4s cubic-bezier(.16,1,.3,1)',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ width: 160, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(step.count)}
                    {step.value !== null && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        ({formatINR(step.value, true)})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default FunnelSection;
